// src/shared/composables/useDateFnsLocale.ts

import { globalComposer } from '@/i18n';
import {
  ar,
  bg,
  ca,
  cs,
  da,
  de,
  deAT,
  el,
  enUS,
  eo,
  es,
  fr,
  frCA,
  he,
  hi,
  hu,
  id,
  it,
  ja,
  ko,
  nl,
  pl,
  pt,
  ptBR,
  ru,
  sl,
  sv,
  th,
  tr,
  uk,
  vi,
  zhCN,
  zhTW,
  type Locale as DateFnsLocale,
} from 'date-fns/locale';
import { computed, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Maps this app's locale codes (etc/config.yaml `supported_locales`, e.g.
 * `zh_TW`, `pt_BR`) to the closest date-fns locale object.
 *
 * Root-cause fix: every `formatDistanceToNow`/`formatDistance` call site
 * across the app previously omitted `{ locale }`, so relative-time strings
 * ("2 hours ago", "in 7 days") always rendered in English regardless of the
 * active vue-i18n locale. Use `useDateFnsLocale()` (component setup) or
 * `getCurrentDateFnsLocale()` (plain utils) to get the right object and pass
 * it as `{ locale: ... }`.
 *
 * Locales with no dedicated date-fns translation (e.g. `mi_NZ`) intentionally
 * fall back to `enUS` rather than guessing a loosely-related regional variant.
 */
const DATE_FNS_LOCALE_MAP: Record<string, DateFnsLocale> = {
  ar,
  bg,
  ca_ES: ca,
  cs,
  da_DK: da,
  de,
  de_AT: deAT,
  el_GR: el,
  en: enUS,
  eo,
  es,
  fr_CA: frCA,
  fr_FR: fr,
  he,
  hi,
  hu,
  id,
  it_IT: it,
  ja,
  ko,
  nl,
  pl,
  pt_BR: ptBR,
  pt_PT: pt,
  ru,
  sl_SI: sl,
  sv_SE: sv,
  th,
  tr,
  uk,
  vi,
  zh: zhCN,
  zh_TW: zhTW,
};

/**
 * Resolves the date-fns Locale object for a given app locale code.
 * Pure function, safe to call outside of Vue's injection context (e.g. from
 * a plain utility module that isn't a composable).
 * Falls back to `enUS` for unmapped/unknown locale codes.
 */
export function resolveDateFnsLocale(locale: string | null | undefined): DateFnsLocale {
  if (!locale) return enUS;
  return DATE_FNS_LOCALE_MAP[locale] ?? enUS;
}

/**
 * Returns the date-fns Locale object for the app's *current* locale, reading
 * directly from the global i18n composer instance. Use this from plain
 * (non-composable) utility functions that can't call `useI18n()` because they
 * run outside a component's setup() — mirrors the `globalComposer` pattern
 * already used in `usePageTitle.ts`.
 */
export function getCurrentDateFnsLocale(): DateFnsLocale {
  return resolveDateFnsLocale(globalComposer.locale.value);
}

/**
 * Composable form for use inside `<script setup>` / component setup(). Returns
 * a computed ref so relative-time strings stay in sync when the visitor
 * switches languages.
 *
 * @example
 * const dateFnsLocale = useDateFnsLocale();
 * const formattedDate = computed(() =>
 *   formatDistanceToNow(props.record.created, {
 *     addSuffix: true,
 *     locale: dateFnsLocale.value,
 *   })
 * );
 */
export function useDateFnsLocale(): ComputedRef<DateFnsLocale> {
  const { locale } = useI18n();
  return computed(() => resolveDateFnsLocale(locale.value));
}
